import { HttpEventType } from '@angular/common/http';
import { Component, HostListener, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import readXlsxFile from 'read-excel-file';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ArticleService } from 'src/app/services/archive/article.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoiceService } from 'src/app/services/invoicing/invoice.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { QuotationService } from 'src/app/services/preliminaries/quotation.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { RevertImportQuotationsComponent } from '../revert-import-quotations/revert-import-quotations.component';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { QuotationImportModel } from 'src/app/models/imports/quotation-import.model';

@Component({
  selector: 'app-import-quotations',
  templateUrl: './import-quotations.component.html',
  styleUrl: './import-quotations.component.scss',
  standalone: false
})
export class ImportQuotationsComponent {

  private readonly branchService = inject(BranchService);
  private readonly companyService = inject(CompanyService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly quotationService = inject(QuotationService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);
  private readonly articleService = inject(ArticleService);
  private readonly enterpriseService = inject(EnterpriseService);

  @ViewChild('revertImportQuotationsModal') revertImportQuotationsModalComponent?: CustomModalComponent | null = null;

  formImport: FormGroup = new FormGroup({});
  branchCode: number | null = null;
  branchId: number = 0;
  companyId: number = 0;
  companyCode: number | null = null;
  componentRevertImportQuotations = RevertImportQuotationsComponent;
  errorquotations: number = 0;
  eventFile: any;
  listBranches: any[] = [];
  listCompanies: any[] = [];
  listEnterprises: any[] = [];
  listArticles: any[] = [];
  listTypes: any[] = [];
  loadQuotationModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  nameFile: string = 'Ningún archivo seleccionado.';
  nameWindow: string = 'Importación Cotizaciones';
  quotationAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  typeId: number = 0;
  typeCode: number = 0;
  isOpened: string | undefined = undefined;
  url: string = "import-quotation";
  visibleTxt: boolean = false;
  enterpriseId: number = 0;
  enterpriseCode: number | null = null;
  errorQuotation: number = 0;
  isImporting: boolean = false;

  async ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getPermission();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  attach() {
    document.getElementById('fileImport')?.click();
  }

  clearForm() {
    this.formImport.controls['companyId'].patchValue(0);
    this.formImport.controls['branchId'].patchValue(0);
    this.formImport.controls['typeId'].patchValue(0);
    this.clearInputFile();
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      this.eventFile.target.value = '';
    }
  }

  downloadErrorsTxt() {
    if (!this.quotationAlerts || this.quotationAlerts.length === 0) {
      this.toastHandlerService.showToast(
        'error',
        'No hay alertas para exportar.'
      );
      return;
    }

    this.invoiceService.exportImportErrors(this.quotationAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionCotizaciones_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast(
          'error',
          'Error al exportar el archivo'
        );
      }
    });
  }

  downloadGuideImport() {
    this.quotationService.downloadGuideImport().subscribe(data => {
      switch (data.type) {
        case HttpEventType.Response: {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = "GuiaImportacionCotizaciones.docx";
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
          break;
        }
      }
    });
  }

  async getAllLists(...listsToLoad: Array<'companies' | 'branches' | 'enterprises' | 'articles' | 'types'>) {
    try {
      const lists = {
        companies: async () => {
          this.listCompanies = await firstValueFrom(
            this.companyService.getAllCompaniesForSelectAsync()
          );
        },
        branches: async () => {
          this.listBranches = await firstValueFrom(
            this.branchService.GetAllBranchesByCompanyIdForSelectAsync(this.companyId)
          );
        },
        enterprises: async () => {
          this.listEnterprises = await firstValueFrom(
            this.enterpriseService.getEnterprisesByBranchIdForSelect(this.branchId)
          );
        },
        articles: async () => {
          this.listArticles = await firstValueFrom(
            this.articleService.getAllArticlesByCompanyForSelectAsync(this.companyId)
          );
        },
        types: async () => {
          this.listTypes = await firstValueFrom(
            this.quotationService.getAllTypesQuotationsForSelect()
          );
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.companies(),
          lists.enterprises(),
          lists.articles(),
          lists.types()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getBranch(branchId: number | null) {
    if (!branchId) {
      this.branchId = 0;
      this.branchCode = 0;
      this.listEnterprises = [];
      return;
    }

    const branch = this.listBranches.find(x => x.value == branchId);
    this.branchId = branchId;
    this.branchCode = branch.code;
    await this.getAllLists('enterprises');
  }

  async getCompany(companyId: number | null) {
    if (!companyId) {
      this.companyId = 0;
      this.companyCode = 0;
      this.listArticles = [];
      this.listBranches = [];
      this.listEnterprises = [];
      this.validateForm();
      return;
    }

    const company = this.listCompanies.find(x => x.value == companyId);
    this.companyId = companyId;
    this.companyCode = company.code;
    this.listArticles = [];
    this.formImport.get('articleId')?.patchValue(null);
    await this.getAllLists('branches', 'articles');
  }

  async getEnterprise(enterpriseId: number | null) {
    if (!enterpriseId) {
      this.enterpriseId = 0;
      this.enterpriseCode = 0;
      return;
    }

    const enterprise = this.listEnterprises.find(x => x.value == enterpriseId);
    this.enterpriseId = enterpriseId;
    this.enterpriseCode = enterprise.code;
  }


  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files.length != 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists('companies', 'types'),
      this.getPermissionProfileByUrl(),
    ])
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(
        this.windowService.getPermissionProfileByUrl(this.url)
      );
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos.', error);
    }
  }

  async getType(typeId: number) {
    if (typeId != null && typeId != 0) {
      const type = this.listTypes.find(x => x.value == typeId);
      this.typeId = typeId;
      this.typeCode = type.value;
    } else {
      this.typeId = 0;
      this.typeCode = 0;
      this.formImport.controls['typeId'].patchValue(0);
    }
  }

  importQuotations() {
    if (this.isImporting) {
      return;
    }
    this.quotationAlerts = [];
    this.errorQuotation = 0;
    this.visibleTxt = false;
    if (this.formImport.valid) {
      let isOk: boolean = true;
      const input = document.getElementById('fileImport') as any;
      const rowsImports = new Array<QuotationImportModel>();
      let row: QuotationImportModel;
      let counter: number = 1, consecutive: number, dateQuotation: Date,
        dateExpiration: Date, quotationStateId: number, conceptCodeConceptRejection: number | null,
        observationRejection: string | null, clientDocument: number | null, nameClient: string,
        constructionSiteCode: number | null, nameConstructionSite: string | null, sellerDocument: string,
        priceListCode: number | null, conceptCodeTypeEquipment: number,
        mountingValue: number, conceptCodeTypeBussiness1: number,
        conceptCodeTypeBussiness2: number, paymentMethod: string | null,
        deliveryTime: string | null, contact: string | null,
        phone1: string | null, phone2: string | null,
        addressConstructionSite: string | null, email: string | null,
        observation: string | null, observationCommercial: string | null,
        swConditions: boolean, showConsolidatedEquipmentAvailability: boolean,
        costCenterCode: number | null, solutionCode: string,
        solutionName: string | null, swBreakdown: boolean,
        baseValue: number, unitCode: string,
        unitValueSolution: number | null, quantitySolution: number | null,
        subtotalValue: number | null, totalValue: number, days: number,
        ivaRate: number | null, ivaValue: number, articleCode: number | null,
        unitValueBreakdown: number, discountRateBreakdown: number,
        clientValueBreakdown: number, quantityBreakdown: number;

      if (input.files[0]) {
        this.isImporting = true;
        readXlsxFile(input.files[0]).then(async (rows) => {
          if (rows.length <= 1) {
            this.toastHandlerService.showToast(
              'error',
              'El archivo ingresado no tiene registros para importar.',
            );
            return;
          }
          rows.splice(0, 1);

          rows.forEach(element => {
            counter = counter + 1;
            let rowHasError = false;

            // A. Código Compañía:
            const companyCode = parseFloat(element[0] as string);
            if (isNaN(companyCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna A. El código de la compañía no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            if (companyCode != this.companyCode) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna A. La compañía del archivo no es igual a la compañía seleccionada.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // B. Código Sucursal:
            const branchCode = parseFloat(element[1] as string);
            if (isNaN(branchCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna B. El código de la sucursal no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            if (branchCode != this.branchCode) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna B. La sucursal del archivo no es igual a la sucursal seleccionada.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // C. Código Tipo:
            const typeCode = parseFloat(element[2] as string);
            if (isNaN(typeCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna C. El código del tipo no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            if (typeCode != this.typeCode) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna C. El tipo de cotización no es igual al tipo seleccionado.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // D. Consecutivo:
            consecutive = parseFloat(element[3] as string);
            if (isNaN(consecutive)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna D. El consecutivo no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // E. Fecha Cotización:
            const dateQuotationString = element[4] as string;
            if (dateQuotationString == null || dateQuotationString == "") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna E. El campo de fecha cotización es obligatorio.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            dateQuotation = moment(dateQuotationString, 'DD/MM/YYYY').toDate();
            if (!moment(dateQuotationString, 'DD/MM/YYYY').isValid()) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna E. El campo de fecha cotización no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // F. Fecha Vencimiento:
            const dateExpirationString = element[5] as string;
            if (dateExpirationString == null || dateExpirationString == "") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna F. El campo de fecha vencimiento es obligatorio.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            dateExpiration = moment(dateExpirationString, 'DD/MM/YYYY').toDate();
            if (!moment(dateExpirationString, 'DD/MM/YYYY').isValid()) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna F. El campo de fecha vencimiento no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // G. Estado Cotización:
            quotationStateId = parseFloat(element[6] as string);
            const validQuotationStates = [1, 2, 3];
            if (isNaN(quotationStateId) || !Number.isInteger(quotationStateId) || !validQuotationStates.includes(quotationStateId)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna G. El estado de la cotización no es válido. Valores permitidos: 1 (Abierto), 2 (Aprobado), 3 (Rechazado).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // H. Código Concepto Rechazo:
            if (element[7] as string != null) {
              conceptCodeConceptRejection = parseFloat(element[7] as string);
              if (isNaN(conceptCodeConceptRejection)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna H. El código del concepto de rechazo no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              conceptCodeConceptRejection = null;
            }

            // I. Observación Rechazo:
            observationRejection = element[8] as string ?? null;
            if (observationRejection != null && observationRejection.length > 250) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna I. La observación de rechazo supera el tamaño máximo de caracteres (250).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // J. Nombre Cliente:
            nameClient = element[9] as string;
            if (nameClient == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna J. El nombre del cliente no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            } else if (nameClient.length > 150) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna J. El nombre del cliente supera el tamaño máximo de caracteres (150)`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // K. Documento Cliente:
            const clientDocumentVal = element[10] as string;
            if (clientDocumentVal != null && clientDocumentVal !== "") {
              clientDocument = parseFloat(clientDocumentVal);
              if (isNaN(clientDocument)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna K. El documento del cliente no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              clientDocument = null;
            }

            // L. Código Obra:
            const constructionSiteVal = element[11] as string;
            if (constructionSiteVal != null && constructionSiteVal !== "") {
              constructionSiteCode = parseFloat(constructionSiteVal);
              if (isNaN(constructionSiteCode)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna L. El código de la obra no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              constructionSiteCode = null;
            }

            // M. Nombre Obra
            nameConstructionSite = element[12] as string ?? null;
            if (nameConstructionSite != null && nameConstructionSite.length > 150) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna M. El nombre de la obra supera el tamaño máximo de caracteres (150).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // N. Documento Vendedor:
            sellerDocument = element[13] as string;
            if (sellerDocument == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna N. El documento del vendedor no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            } else if (sellerDocument.length > 15) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna N. El documento del vendedor supera el tamaño máximo de caracteres (15).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // O. Código Lista de Precios:
            const priceListVal = element[14] as string;
            if (priceListVal != null && priceListVal !== "") {
              priceListCode = parseFloat(priceListVal);
              if (isNaN(priceListCode)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna O. El código de la lista de precios no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              priceListCode = null;
            }

            // P. Tipo de Equipo:
            conceptCodeTypeEquipment = parseFloat(element[15] as string);
            if (isNaN(conceptCodeTypeEquipment)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna P. El código del tipo de equipo no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // Q. Valor Montaje:
            mountingValue = parseFloat(element[16] as string);
            if (isNaN(mountingValue)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna Q. El valor de montaje no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // R. Tipo de Negocio 1:
            conceptCodeTypeBussiness1 = parseFloat(element[17] as string);
            if (isNaN(conceptCodeTypeBussiness1)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna R. El código del tipo de negocio 1 no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // S. Tipo de Negocio 2:
            conceptCodeTypeBussiness2 = parseFloat(element[18] as string);
            if (isNaN(conceptCodeTypeBussiness2)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna S. El código del tipo de negocio 2 no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // T. Método Pago:
            paymentMethod = element[19] as string ?? null;
            if (paymentMethod != null && paymentMethod.length > 70) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna T. El método de pago supera el tamaño máximo de caracteres (70).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // U. Tiempo de Entrega:
            deliveryTime = element[20] as string ?? null;
            if (deliveryTime != null && deliveryTime.length > 70) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna U. El tiempo de entrega supera el tamaño máximo de caracteres (70).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // V. Contacto:
            contact = element[21] as string ?? null;
            if (contact != null && contact.length > 70) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna V. El contacto supera el tamaño máximo de caracteres (70).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // W. Teléfono 1:
            phone1 = element[22] as string ?? null;
            if (phone1 != null && phone1.length > 15) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna W. El teléfono 1 supera el tamaño máximo de caracteres (15).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // X. Teléfono 2:
            phone2 = element[23] as string ?? null;
            if (phone2 != null && phone2.length > 15) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna X. El teléfono 2 supera el tamaño máximo de caracteres (15).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // Y. Dirección Obra:
            addressConstructionSite = element[24] as string ?? null;
            if (addressConstructionSite != null && addressConstructionSite.length > 70) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna Y. La dirección de la obra supera el tamaño máximo de caracteres (70).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // Z. Correo:
            email = element[25] as string ?? null;
            if (email != null && email.length > 250) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna Z. El correo supera el tamaño máximo de caracteres (250).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AA. Observación:
            observation = element[26] as string ?? null;
            if (observation != null && observation.length > 250) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AA. La observación supera el tamaño máximo de caracteres (250).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AB. Observación Comercial:
            observationCommercial = element[27] as string ?? null;
            if (observationCommercial != null && observationCommercial.length > 800) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AB. La observación comercial supera el tamaño máximo de caracteres (800).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AC. Imprime Condiciones
            if (element[28] as string != "S" && element[28] as string != "N") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AC. El indicador de imprime condiciones debe ser valores como: S / N.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            swConditions = element[28] as string === "S";

            // AD. Muestra Disponible Consolidado
            if (element[29] as string != "S" && element[29] as string != "N") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AD. El indicador de muestra disponible consolidado debe ser valores como: S / N.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            showConsolidatedEquipmentAvailability = element[29] as string === "S";

            // AE. Código Centro de Costos
            const costCenterCodeRaw = element[30] as string;
            if (costCenterCodeRaw != null && costCenterCodeRaw !== '') {
              costCenterCode = parseFloat(costCenterCodeRaw);
              if (isNaN(costCenterCode)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AE. El código del centro de costos no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              costCenterCode = null;
            }

            // AF. Código Solución:
            solutionCode = element[31] as string;
            if (solutionCode == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AF. El código de la solución no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            else if (solutionCode != null && solutionCode.length > 5) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AF. El código de la solución supera el tamaño máximo de caracteres (5).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AG. Nombre Solución:
            solutionName = element[32] as string ?? null;
            if (solutionName != null && solutionName.length > 1000) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AG. El nombre de la solución supera el tamaño máximo de caracteres (1000).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AH. Sw Despiece
            if (element[33] as string != "S" && element[33] as string != "N") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AH. El indicador de despiece debe ser valores como: S / N.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }
            swBreakdown = element[33] as string === "S";

            // AI. Valor Base solución:
            const validBaseValues = [1, 2];
            baseValue = parseFloat(element[34] as string);
            if (isNaN(baseValue) || !Number.isInteger(baseValue) || !validBaseValues.includes(baseValue)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AI. El valor base de la solución no es válido. Valores permitidos: 1 (Vr. Unitario), 2 (Subtotal).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AJ. Código Unidad Solución:
            unitCode = element[35] as string ?? null;
            if (unitCode != null && unitCode.length > 4) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AJ. El código de la unidad de la solución supera el tamaño máximo de caracteres (4).`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AK. Valor Unitario Solución:
            const unitValueSolutionRaw = element[36] as string;
            if (unitValueSolutionRaw != null && unitValueSolutionRaw !== "") {
              unitValueSolution = parseFloat(unitValueSolutionRaw);
              if (isNaN(unitValueSolution)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AK. El valor unitario de la solución no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              unitValueSolution = null;
            }

            // AL. Cantidad Solución:
            const quantitySolutionRaw = element[37] as string;
            if (quantitySolutionRaw != null && quantitySolutionRaw !== "") {
              quantitySolution = parseFloat(quantitySolutionRaw);
              if (isNaN(quantitySolution)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AL. La cantidad de la solución no es válida.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              quantitySolution = null;
            }

            // AM. Subtotal Solución:
            const subtotalValueRaw = element[38] as string;
            if (subtotalValueRaw != null && subtotalValueRaw !== "") {
              subtotalValue = parseFloat(subtotalValueRaw);
              if (isNaN(subtotalValue)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AM. El subtotal de la solución no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              subtotalValue = null;
            }

            // AN. Total Solución:
            totalValue = parseFloat(element[39] as string);
            if (isNaN(totalValue)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AN. El total de la solución no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AO. Días de la solución:
            days = parseFloat(element[40] as string);
            if (isNaN(days)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AO. Los días de la solución no son válidos.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AP. Tasa IVA Solución:
            const ivaRateRaw = element[41] as string;
            if (ivaRateRaw != null && ivaRateRaw !== "") {
              ivaRate = parseFloat(ivaRateRaw);
              if (isNaN(ivaRate)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AP. La tasa de IVA de la solución no es válida.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              ivaRate = null;
            }

            // AQ. Valor IVA:
            ivaValue = parseFloat(element[42] as string);
            if (isNaN(ivaValue)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AQ. El valor del IVA no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AR. Código Artículo del despiece:
            const articleCodeRaw = element[43] as string;
            if (articleCodeRaw != null && articleCodeRaw !== "") {
              articleCode = parseFloat(articleCodeRaw);
              if (isNaN(articleCode)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AR. El código del artículo del despiece no es válido.`;
                this.quotationAlerts.push(alert);
                rowHasError = true;
              }
            } else {
              articleCode = null;
            }

            // AS. Valor Unitario Despiece:
            unitValueBreakdown = parseFloat(element[44] as string);
            if (isNaN(unitValueBreakdown)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AS. El valor unitario del despiece no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AT. Tasa Descuento Despiece:
            discountRateBreakdown = parseFloat(element[45] as string);
            if (isNaN(discountRateBreakdown)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AT. La tasa de descuento del despiece no es válida.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AU. Valor Cliente Despiece:
            clientValueBreakdown = parseFloat(element[46] as string);
            if (isNaN(clientValueBreakdown)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AU. El valor del cliente del despiece no es válido.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            // AV. Cantidad Despiece:
            quantityBreakdown = parseFloat(element[47] as string);
            if (isNaN(quantityBreakdown)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna AV. La cantidad del despiece no es válida.`;
              this.quotationAlerts.push(alert);
              rowHasError = true;
            }

            if (!rowHasError) {
              row = new QuotationImportModel(counter, this.companyId!, this.branchId, consecutive, this.typeId,
                conceptCodeConceptRejection, observationRejection, dateQuotation, dateExpiration, quotationStateId,
                clientDocument, nameClient, constructionSiteCode, nameConstructionSite, sellerDocument,
                priceListCode, conceptCodeTypeEquipment, mountingValue, conceptCodeTypeBussiness1,
                conceptCodeTypeBussiness2, paymentMethod, deliveryTime, contact, phone1, phone2,
                addressConstructionSite, email, observation, observationCommercial, swConditions,
                showConsolidatedEquipmentAvailability, costCenterCode, solutionCode, solutionName,
                swBreakdown, baseValue, unitCode, unitValueSolution, quantitySolution, subtotalValue, totalValue,
                days, ivaRate, ivaValue, articleCode, unitValueBreakdown, discountRateBreakdown,
                clientValueBreakdown, quantityBreakdown);
              rowsImports.push(row);
            } else {
              isOk = false;
            }
          });

          if (isOk && rowsImports.length > 0) {
            try {
              const response = await firstValueFrom(this.quotationService.importQuotations(rowsImports));
              this.loadQuotationModel = response;
              this.quotationAlerts = response?.alerts ?? [];
              this.errorQuotation = response?.preInvoicingStateId;

              if (this.errorQuotation === 1) {
                this.visibleTxt = true;
                this.toastHandlerService.showToast(
                  'error',
                  'La importación terminó con errores, puede exportarlos si lo desea.',
                  'ERROR'
                );
              } else if (this.quotationAlerts.length > 0) {
                this.visibleTxt = true;
                this.toastHandlerService.showToast(
                  'error',
                  'La importación terminó con alertas, puede exportarlos si lo desea.'
                );
              } else {
                this.visibleTxt = false;
                this.toastHandlerService.showToast(
                  'success',
                  'La importación terminó exitosamente.'
                );
              }
              this.clearForm();
            } catch (error) {
              this.nameFile = 'Ningún archivo seleccionado.';
              console.error('Error al importar cotizaciones.', error);
            }
          } else {
            this.errorQuotation = 1;
            this.visibleTxt = true;
            this.toastHandlerService.showToast(
              'error',
              'La importación terminó con errores, puede exportarlos si lo desea'
            );
          }
        }).finally(() => {
          this.isImporting = false;
        });
      } else {
        this.toastHandlerService.showToast(
          'error',
          'No ha seleccionado ningún archivo.'
        );
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      companyId: {
        required: 'La compañía es obligatoria.',
        min: 'La compañía es obligatiria.',
      },
      branchId: {
        required: 'La sucursal es obligatoria.',
        min: 'La sucursal es obligatoria.'
      },
      typeId: {
        required: 'El tipo de cotización es obligatorio.',
        min: 'El tipo de cotización es obligatorio.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formImport.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  revertImportQuotations() {
    this.revertImportQuotationsModalComponent?.show();
  }

  validateForm() {
    this.formImport = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      branchId: [0, [Validators.required, Validators.min(1)]],
      typeId: [0, [Validators.required, Validators.min(1)]]
    });
  }

}
